import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminApi, fileUrl } from "../../lib/interview/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Logo, Overline, RecordingDot } from "../../components/himt/Primitives";
import { fmtIST, fmtISTTime } from "../../lib/interview/time";
import { toast } from "sonner";
import { ArrowLeft, Bot, UserCheck, Save, RefreshCw, Video, Monitor, ScanSearch, Image as ImageIcon, IdCard, Ban, Undo2, FileDown, AppWindow, MonitorX } from "lucide-react";

export default function AdminAttemptGrade() {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [humanScores, setHumanScores] = useState({});
  const [humanFeedback, setHumanFeedback] = useState({});
  // AI-score overrides — editable per-answer. Initialised from the
  // backend's persisted ai_score so a no-op save doesn't accidentally mark
  // answers as overridden. Only answers whose ai_score diverges from the
  // original get POSTed with ai_score_overridden=true.
  const [aiScores, setAiScores] = useState({});
  const [aiFeedback, setAiFeedback] = useState({});
  const [saving, setSaving] = useState(false);
  const [runningGaze, setRunningGaze] = useState(false);
  const [runningScreenScan, setRunningScreenScan] = useState(false);
  const token = localStorage.getItem("himt_admin_token") || localStorage.getItem("token");

  const load = async () => {
    try {
      const { data } = await adminApi().get(`/admin/attempts/${attemptId}`);
      setAttempt(data);
      const scores = {}; const fb = {}; const ai = {}; const aifb = {};
      (data.answers || []).forEach((a) => {
        scores[a.id] = a.human_score ?? a.ai_score ?? 0;
        fb[a.id] = a.human_feedback ?? "";
        ai[a.id] = a.ai_score ?? 0;
        aifb[a.id] = a.ai_feedback ?? "";
      });
      setHumanScores(scores); setHumanFeedback(fb);
      setAiScores(ai); setAiFeedback(aifb);
    } catch (e) { toast.error("Failed to load attempt"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [attemptId]);

  const saveGrade = async () => {
    setSaving(true);
    try {
      // Only send AI overrides for answers where the admin actually
      // changed the value — avoids accidentally stamping
      // `ai_score_overridden=true` on every answer during a routine save.
      const aiOverrides = {};
      const aiFeedbackOverrides = {};
      (attempt?.answers || []).forEach((a) => {
        const current = Number(aiScores[a.id] ?? 0);
        const original = Number(a.ai_score ?? 0);
        if (current !== original) {
          aiOverrides[a.id] = current;
          aiFeedbackOverrides[a.id] = aiFeedback[a.id] ?? "";
        }
      });
      const body = { scores: humanScores, feedback: humanFeedback };
      if (Object.keys(aiOverrides).length) {
        body.ai_scores = aiOverrides;
        body.ai_feedback = aiFeedbackOverrides;
      }
      await adminApi().post(`/admin/attempts/${attemptId}/grade`, body);
      toast.success(
        Object.keys(aiOverrides).length
          ? `Saved · ${Object.keys(aiOverrides).length} AI score(s) overridden, final score recomputed.`
          : "Human grades saved, final score computed."
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const downloadReport = async () => {
    toast.loading("Generating full report…", { id: "report" });
    try {
      const res = await adminApi().get(`/admin/attempts/${attemptId}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      const w = window.open(url, "_blank");
      if (!w) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `himt_report_${attempt?.student_name || "candidate"}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success("Report opened — use your browser's Print dialog to Save as PDF.", { id: "report", position: "bottom-center" });
    } catch (e) {
      toast.error("Failed to generate report", { id: "report" });
    }
  };

  const regrade = async () => {
    try {
      await adminApi().post(`/admin/attempts/${attemptId}/regrade-ai`);
      toast.success("AI grades refreshed");
      await load();
    } catch (e) { toast.error("Failed"); }
  };

  const runScreenScan = async () => {
    if (runningScreenScan) return;
    if (!window.confirm("Run AI screen-share analysis on this candidate now? It re-uses captured frames, or extracts frames from the screen recording (may take up to a minute).")) return;
    try {
      setRunningScreenScan(true);
      const { data: res } = await adminApi().post(`/admin/attempts/${attemptId}/run-screen-scan`);
      if (res.status === "done") {
        toast.success(`Done · ${res.scanned} frame(s) analysed · ${res.sharing} screen-sharing detection(s) (source: ${res.source}).`);
      } else if (res.status === "skipped") {
        toast.info(`Skipped: ${res.reason || "nothing to analyse"}`);
      } else {
        toast.error(`Failed: ${res.reason || "unknown"}`);
      }
      await load();
    } catch (e) {
      toast.error("Screen-scan failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRunningScreenScan(false);
    }
  };

  const disqualify = async () => {
    const reason = window.prompt(
      `Disqualify ${attempt?.student_name}?\n\nEnter a reason (stays on the record and shows on the rank sheet):`,
      ""
    );
    if (!reason || reason.trim().length < 3) return;
    try {
      await adminApi().post(`/admin/attempts/${attemptId}/disqualify`, { reason: reason.trim() });
      toast.success(`${attempt.student_name} disqualified.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disqualify failed");
    }
  };

  const reinstate = async () => {
    if (!window.confirm(`Reinstate ${attempt?.student_name}? Their rank will be restored.`)) return;
    try {
      await adminApi().post(`/admin/attempts/${attemptId}/reinstate`);
      toast.success(`${attempt.student_name} reinstated.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reinstate failed");
    }
  };

  if (!attempt) return <div className="p-10 text-slate-500">Loading attempt…</div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo />
            <Button variant="ghost" size="sm" onClick={() => nav("/admin/interview")} className="rounded-sm" data-testid="admin-back-to-dashboard">
              <ArrowLeft size={14} className="mr-1" /> Back
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={downloadReport} className="rounded-sm" data-testid="download-full-report-btn">
              <FileDown size={14} className="mr-1" /> Download Full Report
            </Button>
            <Button variant="outline" size="sm" onClick={regrade} className="rounded-sm" data-testid="regrade-ai-btn">
              <RefreshCw size={14} className="mr-1" /> Re-run AI Grading
            </Button>
            {attempt.disqualified ? (
              <Button
                variant="outline" size="sm"
                onClick={reinstate}
                className="rounded-sm border-slate-300"
                data-testid="reinstate-candidate-btn"
              >
                <Undo2 size={14} className="mr-1" /> Reinstate
              </Button>
            ) : (
              <Button
                variant="outline" size="sm"
                onClick={disqualify}
                className="rounded-sm border-red-400 text-red-600 hover:bg-red-50"
                data-testid="disqualify-candidate-btn"
              >
                <Ban size={14} className="mr-1" /> Disqualify
              </Button>
            )}
            <Button onClick={saveGrade} disabled={saving} className="bg-[#0A2540] text-white rounded-sm" data-testid="save-grade-btn">
              <Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save Human Grade"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8">
        {attempt.disqualified && (
          <div
            className="bg-red-600 text-white rounded-sm p-5 mb-6 flex items-start gap-4"
            data-testid="dq-banner"
          >
            <Ban size={28} className="shrink-0 mt-0.5" />
            <div>
              <div className="font-display font-black text-xl tracking-tight">Candidate disqualified</div>
              <div className="font-mono text-sm mt-1 opacity-90">
                Reason: <b>{attempt.disqualified_reason || "—"}</b>
              </div>
              <div className="font-mono text-[11px] mt-1 opacity-75">
                By {attempt.disqualified_by_email || "admin"} · {fmtIST(attempt.disqualified_at)}
              </div>
              <div className="font-mono text-[11px] mt-1 opacity-75">
                This candidate is forced to the bottom of the rank sheet. Scores are preserved but not ranked. Click "Reinstate" to undo.
              </div>
            </div>
          </div>
        )}

        {/* Candidate header */}
        <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6 grid md:grid-cols-4 gap-6">
          <div>
            <Overline>Candidate</Overline>
            <div className="font-display font-black text-[#0A2540] text-2xl tracking-tight mt-1">{attempt.student_name}</div>
            <div className="font-mono text-xs text-slate-500 mt-1">{attempt.student_email}</div>
          </div>
          <div>
            <Overline>Status</Overline>
            <div className="mt-2"><Badge className="rounded-sm bg-[#0A2540] text-white">{attempt.status}</Badge></div>
          </div>
          <div>
            <Overline>Scores</Overline>
            <div className="font-mono mt-1 text-sm">
              AI: <b>{attempt.ai_total_score ?? "—"}</b> · Human: <b>{attempt.human_total_score ?? "—"}</b><br />
              Final: <span className="text-[#2563EB] font-black">{attempt.final_score ?? "—"}</span> / {attempt.max_possible_score}
            </div>
          </div>
          <div>
            <Overline>Proctoring</Overline>
            <div className="mt-1 text-sm">
              <span className={`font-mono font-bold ${(attempt.proctoring_flags?.length || 0) > 3 ? "text-red-600" : "text-slate-700"}`}>
                {attempt.proctoring_flags?.length || 0} flag(s)
              </span>
              {attempt.id_card_path
                ? <Badge className="ml-2 rounded-sm bg-green-600 text-white">ID Captured</Badge>
                : <Badge className="ml-2 rounded-sm bg-amber-500 text-white">ID Missing</Badge>}
            </div>
          </div>
        </div>

        {/* Gaze / eye-movement analysis — server-side post-process of the
            uploaded webcam recording. ≥60% off-screen triggers the same
            50% score cap as the AI-content rule. */}
        <div
          className={`mb-6 rounded-sm p-4 border-2 ${
            attempt.gaze_analysis_status === "failed" ? "border-red-300 bg-red-50" :
            (attempt.gaze_off_screen_pct ?? 0) >= 60 ? "border-red-300 bg-red-50" :
            (attempt.gaze_off_screen_pct ?? 0) >= 30 ? "border-amber-300 bg-amber-50" :
            attempt.gaze_off_screen_pct != null ? "border-emerald-200 bg-emerald-50" :
            "border-slate-200 bg-slate-50"
          }`}
          data-testid="gaze-analysis-panel"
        >
          <div className="flex items-center justify-between gap-3">
            <Overline><ScanSearch size={12} className="inline mr-1" /> Eye-movement analysis</Overline>
            <div className="flex items-center gap-2">
              {attempt.gaze_off_screen_pct != null ? (
                <span
                  className={`font-mono font-black text-2xl tabular-nums ${
                    attempt.gaze_off_screen_pct >= 60 ? "text-red-700" :
                    attempt.gaze_off_screen_pct >= 30 ? "text-amber-700" :
                    "text-emerald-700"
                  }`}
                  data-testid="gaze-off-screen-pct"
                >
                  {attempt.gaze_off_screen_pct}% off-screen
                </span>
              ) : (
                <span className="text-xs font-mono text-slate-500" data-testid="gaze-status-label">
                  {attempt.gaze_analysis_status === "skipped" ? "skipped" :
                   attempt.gaze_analysis_status === "failed" ? "failed" :
                   attempt.gaze_analysis_status ? attempt.gaze_analysis_status : "not yet analysed"}
                </span>
              )}
              <Button
                size="sm" variant="outline"
                className="rounded-sm text-xs"
                disabled={runningGaze}
                onClick={async () => {
                  if (!window.confirm("Run eye-movement analysis on this candidate's webcam recording now? Takes 3-10s.")) return;
                  try {
                    setRunningGaze(true);
                    const { data: res } = await adminApi().post(`/admin/attempts/${attempt.id}/run-gaze`);
                    if (res.status === "done") {
                      toast.success(`Done · ${res.off_screen_pct}% off-screen` + (res.cap_applied ? " (50% cap applied)" : ""));
                    } else if (res.status === "skipped") {
                      toast.info(`Skipped: ${res.reason || "unknown"}`);
                    } else {
                      toast.error(`Failed: ${res.reason || "unknown"}`);
                    }
                    await load();
                  } catch (e) {
                    toast.error("Run gaze failed: " + (e?.response?.data?.detail || e.message));
                  } finally {
                    setRunningGaze(false);
                  }
                }}
                data-testid="run-gaze-btn"
              >
                {runningGaze ? "Running…" : (attempt.gaze_analysis_status ? "Re-run gaze" : "Run gaze")}
              </Button>
            </div>
          </div>
          {attempt.gaze_off_screen_pct != null && (
            <div className="mt-3 h-2 bg-slate-200 rounded-sm overflow-hidden">
              <div
                className={
                  attempt.gaze_off_screen_pct >= 60 ? "bg-red-500 h-full" :
                  attempt.gaze_off_screen_pct >= 30 ? "bg-amber-500 h-full" :
                  "bg-emerald-500 h-full"
                }
                style={{ width: `${Math.min(100, attempt.gaze_off_screen_pct)}%` }}
              />
            </div>
          )}
          {attempt.gaze_indicators && (
            <p className="mt-2 text-[12px] text-slate-700 italic" data-testid="gaze-indicators">
              {attempt.gaze_indicators}
            </p>
          )}
          {(attempt.gaze_max_off_streak_sec || 0) > 0 && (
            <p className="mt-1 text-[11px] font-mono text-slate-500">
              Longest off-screen streak: <b>{attempt.gaze_max_off_streak_sec}s</b>
              {" · "}analysed {attempt.gaze_analyzed_frames} frame(s)
              {attempt.gaze_no_face_frames ? ` · camera obscured for ${attempt.gaze_no_face_frames} sample(s)` : ""}
            </p>
          )}
          {attempt.gaze_cap_applied && (
            <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded-sm text-[11px] font-mono text-red-900" data-testid="gaze-cap-applied">
              Score cap applied: every AI score on this attempt was capped at 50% of max because off-screen gaze ≥ 60%. Raw values preserved as <code>ai_score_raw</code>.
            </div>
          )}
          {(attempt.gaze_analysis_status === "failed" || attempt.gaze_analysis_status === "skipped") && attempt.gaze_skip_reason && (
            <div
              className={`mt-2 p-2 rounded-sm text-[11px] font-mono leading-snug ${
                attempt.gaze_analysis_status === "failed"
                  ? "bg-red-100 border border-red-300 text-red-900"
                  : "bg-slate-100 border border-slate-300 text-slate-700"
              }`}
              data-testid="gaze-skip-reason"
            >
              <b>{attempt.gaze_analysis_status === "failed" ? "Failure reason:" : "Skip reason:"}</b> {attempt.gaze_skip_reason}
              {attempt.gaze_analysis_status === "failed" && (
                <span className="block mt-1">Click "Re-run gaze" above to retry. If it fails repeatedly, the recording file may be corrupt or the candidate's webcam was obscured for the entire session.</span>
              )}
            </div>
          )}
          {!attempt.webcam_recording_path && (
            <div className="mt-2 p-2 bg-slate-100 border border-slate-300 rounded-sm text-[11px] font-mono text-slate-700" data-testid="gaze-no-recording">
              <b>No webcam recording on file</b> for this attempt — gaze analysis cannot run. The candidate may have disabled webcam recording, or the upload failed during their session.
            </div>
          )}
        </div>

        {/* Captured ID card — image only. OCR/match-ratio columns removed;
            admins cross-check the photo against the registered name
            manually during review. */}
        {attempt.id_card_path && (
          <div className="bg-white border border-slate-200 rounded-sm p-5 mb-6 grid md:grid-cols-3 gap-5" data-testid="id-card-section">
            <div className="md:col-span-1">
              <Overline><IdCard size={12} className="inline mr-1" /> Captured ID card</Overline>
              <img
                src={fileUrl(attempt.id_card_path, token)}
                alt="Captured ID card"
                className="mt-3 w-full max-h-72 object-contain bg-slate-100 border border-slate-200 rounded-sm"
                data-testid="id-card-image"
              />
            </div>
            <div className="md:col-span-2">
              <Overline>Registered candidate</Overline>
              <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-xs tracking-wider uppercase text-slate-500">Name on invite</dt>
                  <dd className="font-mono text-[#0A2540] font-bold mt-0.5">{attempt.student_name || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs tracking-wider uppercase text-slate-500">Email</dt>
                  <dd className="font-mono text-slate-700 mt-0.5">{attempt.student_email || "—"}</dd>
                </div>
              </dl>
              <div className="mt-4 text-xs text-slate-500 font-mono leading-relaxed">
                Visually verify that the photo and name on the captured ID match the registered candidate above.
              </div>
            </div>
          </div>
        )}

        {/* Recordings */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <RecordingCard
            label="Webcam Recording"
            icon={Video}
            path={attempt.webcam_recording_path}
            token={token}
            attemptId={attempt.id}
            kind="webcam"
          />
          <RecordingCard
            label="Screen Recording"
            icon={Monitor}
            path={attempt.screen_recording_path}
            token={token}
            attemptId={attempt.id}
            kind="screen"
          />
        </div>

        {/* AI Screen Scan — visible apps + screen-sharing/remote-control detection */}
        {Array.isArray(attempt.screen_scans) && attempt.screen_scans.length > 0 && (() => {
          const scans = attempt.screen_scans;
          const sharing = scans.filter((s) => s.screen_sharing_detected);
          const partial = scans.some((s) => s.display_surface && s.display_surface !== "monitor" && s.display_surface !== "screen");
          const apps = Array.from(new Set(scans.flatMap((s) => s.visible_apps || []))).sort();
          return (
            <div
              className={`bg-white border rounded-sm p-5 mb-6 ${sharing.length ? "border-red-300" : "border-slate-200"}`}
              data-testid="screen-scan-panel"
            >
              <div className="flex items-center justify-between">
                <Overline><AppWindow size={12} className="inline mr-1" /> AI Screen Scan · {scans.length} capture(s)</Overline>
                <div className="flex items-center gap-2">
                  {sharing.length ? (
                    <Badge className="rounded-sm bg-red-600 text-white" data-testid="screen-share-detected-badge">
                      <MonitorX size={12} className="inline mr-1" /> Screen-sharing detected
                    </Badge>
                  ) : (
                    <Badge className="rounded-sm bg-green-600 text-white">No sharing tools detected</Badge>
                  )}
                  <Button size="sm" variant="outline" className="rounded-sm text-xs" disabled={runningScreenScan} onClick={runScreenScan} data-testid="run-screen-scan-btn">
                    {runningScreenScan ? "Analysing…" : "Re-run analysis"}
                  </Button>
                </div>
              </div>
              {partial && (
                <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-mono font-bold text-amber-700 bg-amber-50 border border-amber-300 rounded-sm px-2 py-1" data-testid="partial-screen-share-note">
                  <MonitorX size={12} /> Candidate did not share their entire screen (shared a window/tab) on at least one scan.
                </div>
              )}
              <p className="mt-2 text-xs text-slate-600 font-mono">Apps seen on screen: <b>{apps.join(", ") || "—"}</b></p>
              <div className="mt-3 space-y-2">
                {scans.map((s, i) => (
                  <div
                    key={s.id || i}
                    className={`border rounded-sm p-3 text-xs ${s.screen_sharing_detected ? "border-red-300 bg-red-50" : s.suspicious ? "border-amber-300 bg-amber-50" : "border-slate-200"}`}
                    data-testid={`screen-scan-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      {s.image_path && (
                        <img
                          src={fileUrl(s.image_path, token)}
                          alt={`Screen scan ${i + 1}`}
                          className="w-40 h-24 object-cover rounded-sm border border-slate-200 bg-slate-100 shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <div className="font-mono text-[11px] text-slate-500">
                          {fmtISTTime(s.at)} IST{s.display_surface ? ` · shared: ${s.display_surface}` : ""}{s.phase ? ` · ${s.phase}` : ""}
                        </div>
                        {s.screen_sharing_detected && (
                          <div className="text-red-700 font-bold mt-0.5">⚠ Screen-sharing tool: {(s.screen_sharing_apps || []).join(", ") || "detected"}</div>
                        )}
                        <div className="text-slate-700 mt-0.5">Apps: {(s.visible_apps || []).join(", ") || "—"}</div>
                        {s.summary && <div className="text-slate-500 italic mt-0.5">{s.summary}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* No screen scans yet, but a screen recording exists → offer analysis */}
        {(!attempt.screen_scans || attempt.screen_scans.length === 0) && attempt.screen_recording_path && (
          <div className="bg-white border border-slate-200 rounded-sm p-5 mb-6" data-testid="screen-scan-empty-panel">
            <div className="flex items-center justify-between">
              <Overline><AppWindow size={12} className="inline mr-1" /> AI Screen Scan</Overline>
              <Button size="sm" variant="outline" className="rounded-sm text-xs" disabled={runningScreenScan} onClick={runScreenScan} data-testid="run-screen-scan-btn">
                {runningScreenScan ? "Analysing…" : "Run screen-share analysis"}
              </Button>
            </div>
            <p className="mt-2 text-xs text-slate-500 font-mono">No screen scans on file. Run analysis to extract frames from the screen recording and detect open apps / screen-sharing software.</p>
          </div>
        )}

        {/* 360° Environment Scan — mobile handoff video OR webcam stills fallback */}
        {(attempt.env_scan_video_path || (attempt.env_scan_paths && attempt.env_scan_paths.length > 0)) && (
          <div className="bg-white border border-slate-200 rounded-sm p-5 mb-6" data-testid="env-scan-section">
            <div className="flex items-center justify-between">
              <Overline><ScanSearch size={12} className="inline mr-1" /> 360° Environment Scan</Overline>
              <Badge variant="outline" className="rounded-sm font-mono text-[10px]">
                {attempt.env_scan_video_path ? "Mobile · Video" : "Webcam · Stills"}
              </Badge>
            </div>
            {attempt.env_scan_video_path && (
              <div className="mt-3">
                <video
                  controls
                  preload="metadata"
                  playsInline
                  src={fileUrl(attempt.env_scan_video_path, token)}
                  className="w-full max-h-[420px] rounded-sm bg-black"
                  data-testid="env-scan-video"
                />
                <div className="text-xs text-slate-500 font-mono mt-2">
                  10-second 360° room sweep captured via mobile QR handoff.
                </div>
              </div>
            )}
            {!attempt.env_scan_video_path && attempt.env_scan_paths && attempt.env_scan_paths.length > 0 && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                {attempt.env_scan_paths.map((s, i) => (
                  <div key={i} className="border border-slate-200 rounded-sm overflow-hidden" data-testid={`env-scan-still-${i}`}>
                    <img
                      src={fileUrl(s.storage_path || s.path, token)}
                      alt={`Env scan ${s.direction || i + 1}`}
                      className="w-full aspect-video object-cover bg-slate-100"
                    />
                    <div className="px-2 py-1.5 text-[10px] font-mono uppercase tracking-wider text-slate-500 border-t border-slate-200 bg-slate-50 flex items-center gap-1">
                      <ImageIcon size={11} /> {s.direction || `Still ${i + 1}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Client-side error telemetry — shown ONLY when the candidate's
            browser reported a startup / media failure. Lets the invigilator
            pinpoint why a test didn't begin without chasing screenshots. */}
        {Array.isArray(attempt.client_errors) && attempt.client_errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-5 mb-6" data-testid="client-errors-panel">
            <div className="flex items-center gap-2 text-red-800 font-bold text-sm tracking-[0.2em] uppercase font-mono mb-3">
              <RecordingDot /> Candidate reported {attempt.client_errors.length} startup error{attempt.client_errors.length > 1 ? "s" : ""}
            </div>
            <div className="space-y-2">
              {attempt.client_errors.slice(-10).reverse().map((err, i) => (
                <div key={err.id || i} className="bg-white border border-red-200 rounded-sm p-3 text-xs font-mono" data-testid={`client-error-${i}`}>
                  <div className="flex justify-between items-start gap-2">
                    <span className="font-bold text-red-900">{err.phase}</span>
                    <span className="text-slate-500">{fmtISTTime(err.at)}</span>
                  </div>
                  <div className="mt-1 text-slate-800"><b>{err.name || "Error"}:</b> {err.message || "(no message)"}</div>
                  {err.user_agent && (
                    <div className="mt-1 text-slate-500 truncate" title={err.user_agent}>UA: {err.user_agent}</div>
                  )}
                  {err.browser_info?.displaySurface && (
                    <div className="mt-1 text-slate-500">displaySurface: <code>{String(err.browser_info.displaySurface)}</code></div>
                  )}
                  {err.stack && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-slate-500 hover:text-slate-800">stack trace</summary>
                      <pre className="mt-1 text-[10px] whitespace-pre-wrap text-slate-600 max-h-40 overflow-y-auto">{err.stack}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MCQ paper — what the candidate received & answered (combined exams) */}
        {Array.isArray(attempt.mcq_detail) && attempt.mcq_detail.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-5 mb-6" data-testid="mcq-detail-section">
            <div className="flex items-center justify-between">
              <Overline>MCQ Paper · {attempt.mcq_detail.filter((d) => d.is_correct).length}/{attempt.mcq_detail.length} correct</Overline>
              <Badge variant="outline" className="rounded-sm font-mono text-[10px]">Score {attempt.mcq_score ?? "—"} / {attempt.mcq_max ?? "—"}</Badge>
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm border-collapse" data-testid="mcq-detail-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-2 w-8">#</th>
                    <th className="py-2 pr-3">Question</th>
                    <th className="py-2 pr-3">Candidate answer</th>
                    <th className="py-2 pr-3">Correct answer</th>
                    <th className="py-2 pr-2 text-center w-12">OK</th>
                    <th className="py-2 text-center w-12">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {attempt.mcq_detail.map((d, i) => (
                    <tr
                      key={d.question_id || i}
                      className={`border-b border-slate-100 align-top ${d.is_correct ? "" : d.answered ? "bg-red-50" : "bg-amber-50"}`}
                      data-testid={`mcq-row-${i}`}
                    >
                      <td className="py-2 pr-2 font-mono text-slate-400">{i + 1}</td>
                      <td className="py-2 pr-3 text-slate-800">{d.question_text}</td>
                      <td className="py-2 pr-3 font-mono text-slate-700">{d.student_answer_text || <span className="text-slate-400 italic">not answered</span>}</td>
                      <td className="py-2 pr-3 font-mono text-emerald-700">{d.correct_answer_text}</td>
                      <td className="py-2 pr-2 text-center">{d.is_correct ? <span className="text-emerald-600 font-bold">✓</span> : <span className="text-red-500 font-bold">✗</span>}</td>
                      <td className="py-2 text-center font-mono">{d.points_earned ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Split-screen: Answers + Grading */}
        <div className="space-y-5">
          {(attempt.answers || []).map((ans, i) => (
            <div key={ans.id} className="grid lg:grid-cols-2 gap-0 bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`answer-row-${ans.id}`}>
              {/* LEFT: AI */}
              <div className="p-6 border-r border-slate-200 bg-slate-50">
                <div className="flex items-center justify-between">
                  <Overline><Bot size={12} className="inline mr-1" /> AI Evaluation — Q{String(i + 1).padStart(2, "0")}</Overline>
                  {ans.skipped && <Badge className="rounded-sm bg-amber-500 text-white">SKIPPED</Badge>}
                </div>
                <p className="font-display font-bold text-[#0A2540] mt-2 tracking-tight">{ans.question_text}</p>
                {ans.key_concepts?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {ans.key_concepts.map((kc) => (
                      <Badge key={kc} variant="outline" className="rounded-sm font-mono text-[10px]">{kc}</Badge>
                    ))}
                  </div>
                )}
                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    <Overline>Transcription</Overline>
                    {ans.transcription_source === "browser" && (
                      <Badge variant="outline" className="rounded-sm font-mono text-[10px] border-emerald-300 text-emerald-700">Browser STT · real-time</Badge>
                    )}
                    {ans.transcription_source === "audio_only" && (
                      <Badge variant="outline" className="rounded-sm font-mono text-[10px] border-amber-300 text-amber-700">Audio only · listen & grade</Badge>
                    )}
                    {ans.transcription_source === "whisper" && ans.transcription_status === "done" && (
                      <Badge variant="outline" className="rounded-sm font-mono text-[10px]">Whisper</Badge>
                    )}
                    {ans.transcription_status === "pending" && (
                      <Badge variant="outline" className="rounded-sm font-mono text-[10px] border-amber-300 text-amber-700">transcribing…</Badge>
                    )}
                    {ans.transcription_status === "failed" && (
                      <Badge variant="outline" className="rounded-sm font-mono text-[10px] border-red-300 text-red-700">STT failed</Badge>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-slate-700 font-mono leading-relaxed italic min-h-[2em]" data-testid={`transcript-${ans.id}`}>
                    {ans.transcription || <span className="text-slate-400">— no transcription —</span>}
                  </p>
                </div>
                {ans.audio_path && (
                  <audio controls src={fileUrl(ans.audio_path, token)} className="w-full mt-3" data-testid={`audio-${ans.id}`} />
                )}
                <div className="mt-4 p-3 bg-white border border-slate-200 rounded-sm" data-testid={`ai-score-block-${ans.id}`}>
                  <div className="flex items-center justify-between">
                    <Overline>
                      AI Score
                      {ans.ai_score_overridden && (
                        <Badge className="ml-2 rounded-sm bg-amber-500 text-white text-[9px] font-mono tracking-widest">OVERRIDDEN</Badge>
                      )}
                    </Overline>
                    <span className="text-[10px] font-mono tracking-widest text-slate-400 uppercase">/ {ans.max_score}</span>
                  </div>
                  <Input
                    type="number" min={0} max={ans.max_score} step="0.5"
                    value={aiScores[ans.id] ?? 0}
                    onChange={(e) => setAiScores({ ...aiScores, [ans.id]: Number(e.target.value) })}
                    className="rounded-sm font-mono text-xl h-11 mt-2"
                    data-testid={`ai-score-input-${ans.id}`}
                  />
                  <Textarea
                    value={aiFeedback[ans.id] ?? ""}
                    onChange={(e) => setAiFeedback({ ...aiFeedback, [ans.id]: e.target.value })}
                    className="rounded-sm mt-2 text-xs"
                    rows={2}
                    placeholder="AI feedback (editable if you override)"
                    data-testid={`ai-feedback-${ans.id}`}
                  />
                  <p className="text-[10px] text-slate-500 font-mono mt-1.5 tracking-wider uppercase">
                    Change only if the AI made a factual error · final score recomputes on save
                  </p>
                </div>

                {/* AI Content Analysis */}
                {(ans.ai_content_score !== undefined && ans.ai_content_score !== null) && (
                  <div className={`mt-3 p-3 rounded-sm border text-xs font-mono ${
                    ans.ai_content_score >= 70
                      ? "bg-red-50 border-red-300"
                      : ans.ai_content_score >= 35
                      ? "bg-amber-50 border-amber-300"
                      : "bg-emerald-50 border-emerald-300"
                  }`} data-testid={`ai-content-block-${ans.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="uppercase tracking-widest text-[10px] font-bold text-slate-600">AI Content Analysis</span>
                      <span className={`font-black text-sm ${
                        ans.ai_content_score >= 70 ? "text-red-700"
                        : ans.ai_content_score >= 35 ? "text-amber-700"
                        : "text-emerald-700"
                      }`}>
                        {ans.ai_content_score}% AI-like
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-1.5 mb-1.5">
                      <div
                        className={`h-1.5 rounded-full ${
                          ans.ai_content_score >= 70 ? "bg-red-500"
                          : ans.ai_content_score >= 35 ? "bg-amber-500"
                          : "bg-emerald-500"
                        }`}
                        style={{ width: `${ans.ai_content_score}%` }}
                      />
                    </div>
                    {ans.ai_content_indicators && (
                      <p className="text-[11px] text-slate-600 mt-1 italic">{ans.ai_content_indicators}</p>
                    )}
                    {ans.ai_score_capped && (
                      <div
                        className="mt-2 p-2 bg-red-100 border border-red-300 rounded-sm text-[11px] font-mono text-red-900 leading-snug"
                        data-testid={`ai-cap-applied-${ans.id}`}
                      >
                        <b>AI penalty applied:</b> AI score was capped at 50% of max ({(ans.max_score * 0.5)}/{ans.max_score}) because transcript reads ≥60% AI-generated. Raw uncapped AI score: <b>{ans.ai_score_raw ?? "—"}</b>.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* RIGHT: Human */}
              <div className="p-6 bg-white">
                <Overline><UserCheck size={12} className="inline mr-1" /> Human Review</Overline>
                <div className="mt-4">
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Score (out of {ans.max_score})</label>
                  <Input
                    type="number" min={0} max={ans.max_score} step="0.5"
                    value={humanScores[ans.id] ?? 0}
                    onChange={(e) => setHumanScores({ ...humanScores, [ans.id]: Number(e.target.value) })}
                    className="rounded-sm font-mono text-2xl h-14 mt-1"
                    data-testid={`human-score-${ans.id}`}
                  />
                </div>
                <div className="mt-4">
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Evaluator notes</label>
                  <Textarea
                    value={humanFeedback[ans.id] ?? ""}
                    onChange={(e) => setHumanFeedback({ ...humanFeedback, [ans.id]: e.target.value })}
                    className="rounded-sm mt-1" rows={4}
                    data-testid={`human-feedback-${ans.id}`}
                    placeholder="Explain the override or confirmation..."
                  />
                </div>
                <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-sm text-xs text-slate-600">
                  <div className="font-mono">Duration: {Math.round(ans.duration_seconds || 0)}s · Recorded: {fmtISTTime(ans.recorded_at)} IST</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Proctoring flags */}
        {(attempt.proctoring_flags?.length || 0) > 0 && (
          <div className="mt-8 bg-white border border-slate-200 rounded-sm p-6">
            <Overline>Proctoring Timeline</Overline>
            <div className="mt-3 space-y-2">
              {attempt.proctoring_flags.map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-sm font-mono" data-testid={`flag-${i}`}>
                  <span className={`h-2 w-2 rounded-full ${f.severity === "high" ? "bg-red-500" : f.severity === "medium" ? "bg-amber-500" : "bg-slate-400"}`} />
                  <span className="text-slate-500 w-36">{new Date(f.timestamp).toLocaleTimeString()}</span>
                  <Badge variant="outline" className="rounded-sm">{f.type}</Badge>
                  <span className="text-slate-600">{f.details}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function RecordingCard({ label, icon: Icon, path, token, attemptId, kind }) {
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    toast.loading("Preparing download…", { id: `dl-${kind}` });
    try {
      // Use the shared axios instance (`adminApi()`) so the baseURL + auth
      // header behave identically to every other admin call. The previous
      // hand-rolled `fetch(...)` was firing a preflight against a URL the
      // browser treated as cross-origin once deployed behind the reverse
      // proxy, and we silently caught + discarded the error — the user
      // saw the button click register but no download ever started.
      const res = await adminApi().get(`/admin/attempts/${attemptId}/download/${kind}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = m ? m[1] : `${kind}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(href), 2000);
      toast.success(`Downloaded ${a.download}`, { id: `dl-${kind}` });
    } catch (e) {
      const msg = e?.response?.status
        ? `Download failed (HTTP ${e.response.status})`
        : (e?.message || "Download failed");
      toast.error(msg, { id: `dl-${kind}` });
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center justify-between">
        <Overline><Icon size={12} className="inline mr-1" /> {label}</Overline>
        <div className="flex items-center gap-2">
          {path && (
            <button
              onClick={download}
              disabled={downloading}
              type="button"
              className="text-[10px] font-mono font-bold tracking-widest uppercase text-[#2563EB] hover:underline disabled:opacity-50"
              data-testid={`download-${kind}-btn`}
            >
              {downloading ? "Downloading…" : "Download"}
            </button>
          )}
          {path && <RecordingDot />}
        </div>
      </div>
      {path ? (
        <video controls preload="metadata" playsInline src={fileUrl(path, token)} className="w-full mt-3 rounded-sm bg-black" />
      ) : (
        <div className="mt-3 p-8 bg-slate-50 border border-dashed border-slate-300 rounded-sm text-center text-sm text-slate-500">
          No recording uploaded
        </div>
      )}
    </div>
  );
}
